<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useApiStore } from "@/stores/api";
import { storeToRefs } from "pinia";
import { type InventoryItem } from "@/stores/types/api.types";
import { constructUrl } from "@/utils/utils";

const apiStore = useApiStore();

const { baseUrl, rawUrls, inventory, apiEntries, isInventoryLoading } = storeToRefs(apiStore);

const urls = ref<(string | InventoryItem)[]>(rawUrls.value);

function getChipState(item: string | InventoryItem): { color: string; loading: boolean } {
  const url = typeof item === "object" && item !== null ? constructUrl(baseUrl.value, item.path) : String(item);
  const entry = apiEntries.value.get(url);

  if (!entry || entry.state === "idle" || entry.state === "loading") {
    return { color: "#333333", loading: entry?.state === "loading" };
  }

  if (entry.state === "error") {
    return { color: "error", loading: false };
  }

  return { color: "success", loading: false };
}

const validationMessages = computed(() => {
  const messages: string[] = [];
  apiEntries.value.forEach((entry, url) => {
    if (entry.state === "error" && entry.error) {
      const name = typeof entry.item === "object" ? entry.item.name : url;
      messages.push(`${name}: ${entry.error}`);
    }
  });
  return messages;
});

const hasFieldErrors = computed(() => validationMessages.value.length > 0);

watch(urls, async () => {
  apiStore.setRawUrls(urls.value);
  await apiStore.syncUrls(urls.value);
});
</script>

<template>
  <v-card elevation="0" flat>
    <v-card-text>
      <v-combobox
        v-model="urls"
        :items="inventory"
        item-title="name"
        item-value="path"
        label="API Endpoints"
        hint="Select or type API endpoints"
        color="primary"
        persistent-hint
        multiple
        chips
        closable-chips
        clearable
        :error="hasFieldErrors"
        :error-messages="validationMessages"
        :loading="isInventoryLoading"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip v-bind="props" :color="getChipState(item.raw).color">
            <v-progress-circular v-if="getChipState(item.raw).loading" indeterminate size="16" width="2" class="mr-2" />
            <v-tooltip :text="typeof item.raw === 'object' ? item.raw.name : item.raw">
              <template v-slot:activator="{ props: tooltipProps }">
                <p class="text-truncate" v-bind="tooltipProps">
                  {{ typeof item.raw === "object" ? item.raw.name : item.raw }}
                </p>
              </template>
            </v-tooltip>
          </v-chip>
        </template>
      </v-combobox>
    </v-card-text>
  </v-card>
</template>

<style scoped></style>
